import React, { useState, useMemo } from "react";
import {
	Modal,
	ModalHeader,
	ModalBody,
	ModalFooter,
	Button,
	Form,
	FormGroup,
	Label,
	Input,
	Alert,
	Spinner,
	Card,
	CardHeader,
	CardBody,
	Table
} from "reactstrap";
import { useSelector } from "react-redux";
import { IProduct } from "../../interfaces/products";
import { generateProductAPI } from "../../services/productGenerationAPI";
import { IApplicationState } from "../../redux/reducers";
import productService from "../../services/productService";
import "../../assets/scss/modals/_generate-product-modal.scss";

interface GenerateProductModalProps {
	isOpen: boolean;
	toggle: () => void;
	onProductGenerated: (product: IProduct) => void;
}

interface GeneratedProductData {
	code?: string;
	name?: string;
	standard_of_identity?: string | null;
	ingredients?: any[];
	product_image?: string | null;
	company?: any;
	brand?: any;
	product_class?: string;
	product_form?: string | null;
	product_type?: any;
	product_status?: boolean;
	serving_size?: number | string | null;
	unit_serving_size?: string;
	cost?: number;
	currency?: any;
	country?: any;
	countries_sold?: string | null;
	image_uri?: string | null;
	manufacturer?: any;
	packaging?: any | null;
	certifications?: any[];
	functional_tags?: any[];
	active?: boolean;
	weight?: string | number;
	product_url?: string | null;
	notes?: string;
	created_date?: string | null;
	updated_at?: string | null;
	master_product_id?: string | null;
	id?: string;
	created_by?: any;
	updated_by?: any;
	optimized_feature?: any;
	sustainability_score?: number;
	nutrition_score?: number;
	insertion_date?: string;
	version?: number;
	objective?: string[];
	is_guava?: boolean;
	guava_score?: number;
	upc_code?: string;
	additives?: boolean;
	is_ai_generated?: boolean;
	cost_margin?: number;
	markup?: number;
	flavor?: string;
	is_shared_with_team?: boolean;
	shared_with_companies?: any[];
	recommendations_count?: number;
	[key: string]: any; // Allow any additional fields
}

const GenerateProductModal: React.FC<GenerateProductModalProps> = ({
	isOpen,
	toggle,
	onProductGenerated
}) => {
	// Get user's company and brand information from Redux store
	const { user } = useSelector((state: IApplicationState) => state);
	const { profile, selected_brand } = user;
	const company = profile?.company;

	const [productDescription, setProductDescription] = useState("");
	const [isGenerating, setIsGenerating] = useState(false);
	const [generatedProduct, setGeneratedProduct] = useState<GeneratedProductData | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [showResult, setShowResult] = useState(false);

	const handleGenerate = async () => {
		if (!productDescription.trim()) {
			setError("Please enter a product description");
			return;
		}

		setIsGenerating(true);
		setError(null);
		setGeneratedProduct(null);
		setShowResult(false);

		try {
			// Call our product generation API
			const result = await generateProductAPI({
				description: productDescription.trim()
			});

			if (!result.success) {
				throw new Error(result.error || "Failed to generate product");
			}

			// Validate that we have a product with required fields
			if (!result.product || !result.product.name) {
				throw new Error("Invalid product data received from server");
			}

			// If product has an ID but no ingredients, fetch full product details
			let productWithIngredients = result.product;
			if (result.product.id && (!result.product.ingredients || 
				(Array.isArray(result.product.ingredients) && result.product.ingredients.length === 0))) {
				try {
					// Fetch the full product details which should include ingredients
					const fullProduct = await productService.fetchProductById(result.product.id);
					if (fullProduct && fullProduct.ingredients && fullProduct.ingredients.length > 0) {
						productWithIngredients = {
							...result.product,
							ingredients: fullProduct.ingredients
						};
					}
				} catch (fetchError) {
					console.warn("Could not fetch product ingredients:", fetchError);
					// Continue with the original product data if fetch fails
				}
			}

			setGeneratedProduct(productWithIngredients);
			setShowResult(true);
			
			// Debug: Log the generated product to see ingredient structure
		
		} catch (err) {
			console.error("Error generating product:", err);
			const errorMessage =
				err instanceof Error ? err.message : "Failed to generate product. Please try again.";
			setError(errorMessage);
			// Reset state on error
			setGeneratedProduct(null);
			setShowResult(false);
		} finally {
			setIsGenerating(false);
		}
	};

	const handleUseProduct = async () => {
		if (!generatedProduct) {
			return;
		}

		try {
			// Preserve all fields from the generated product
			// The generatedProduct is already formatted by formatGeneratedProductForBackend
			// which preserves all fields from the backend response
			const productData: any = {
				...generatedProduct // Preserve all fields including id, created_by, updated_by, etc.
			};

			// Only override company if it's missing or incomplete, but preserve full object if it exists
			if (!productData.company && company?.id) {
				productData.company = company.id;
			} else if (
				productData.company &&
				typeof productData.company === "object" &&
				!productData.company.id &&
				company?.id
			) {
				// If company object exists but doesn't have id, preserve the object but add id
				productData.company = { ...productData.company, id: company.id };
			} else if (typeof productData.company === "string" && company?.id) {
				// If company is just a string ID, keep it as is (backend expects string ID for creation)
				productData.company = productData.company;
			}

			// Only override brand if it's missing, but preserve full object if it exists
			if (!productData.brand && selected_brand?.id) {
				productData.brand = { id: selected_brand.id };
			} else if (productData.brand === null && selected_brand?.id) {
				productData.brand = { id: selected_brand.id };
			} else if (
				productData.brand &&
				typeof productData.brand === "object" &&
				!productData.brand.id &&
				selected_brand?.id
			) {
				// If brand object exists but doesn't have id, preserve the object but add id
				productData.brand = { ...productData.brand, id: selected_brand.id };
			}

			// Ensure required fields are present (for backend compatibility)
			if (!productData.new_ingredients) {
				productData.new_ingredients = [];
			}
			if (productData.newly_added_product_ingredient === undefined) {
				productData.newly_added_product_ingredient = null;
			}

			// Safely call the callback with error handling
			if (onProductGenerated && typeof onProductGenerated === "function") {
				await Promise.resolve(onProductGenerated(productData as IProduct));
			}

			handleClose();
		} catch (err) {
			console.error("Error in handleUseProduct:", err);
			setError(
				err instanceof Error
					? err.message
					: "Failed to process generated product. Please try again."
			);
			// Don't close the modal if there's an error, so user can see the error message
		}
	};

	const handleClose = () => {
		setProductDescription("");
		setGeneratedProduct(null);
		setError(null);
		setShowResult(false);
		toggle();
	};

	const handleNewGeneration = () => {
		setGeneratedProduct(null);
		setShowResult(false);
		setError(null);
	};

	// Safety check: ensure onProductGenerated is a function
	if (!onProductGenerated || typeof onProductGenerated !== "function") {
		console.warn("GenerateProductModal: onProductGenerated callback is not a function");
	}

	// Normalize ingredients - handle both array and object formats
	const normalizedIngredients = useMemo(() => {
		if (!generatedProduct?.ingredients) {
			return [];
		}
		
		// If it's already an array, return it
		if (Array.isArray(generatedProduct.ingredients)) {
			return generatedProduct.ingredients;
		}
		
		// If it's an object (legacy format), convert to array
		if (typeof generatedProduct.ingredients === "object" && generatedProduct.ingredients !== null && !Array.isArray(generatedProduct.ingredients)) {
			const ingredientsArray: any[] = [];
			const ingredientsObj = generatedProduct.ingredients as { [key: string]: any };
			Object.keys(ingredientsObj).forEach((key) => {
				const flavorIngredients = ingredientsObj[key];
				if (Array.isArray(flavorIngredients)) {
					ingredientsArray.push(...flavorIngredients);
				}
			});
			return ingredientsArray;
		}
		
		return [];
	}, [generatedProduct?.ingredients]);

	// Calculate total weights for percentage calculation
	const totalIngredientWeights = useMemo(() => {
		if (!normalizedIngredients || normalizedIngredients.length === 0) {
			return 0;
		}
		return normalizedIngredients.reduce((total, ing) => {
			const weight = typeof ing.weight === "number" ? ing.weight : parseFloat(ing.weight) || 0;
			return total + weight;
		}, 0);
	}, [normalizedIngredients]);

	// Helper function to get ingredient display name
	const getIngredientName = (ingredient: any): string => {
		if (typeof ingredient.ingredient === "object" && ingredient.ingredient?.jf_display_name) {
			return ingredient.ingredient.jf_display_name;
		}
		if (typeof ingredient.ingredient === "string") {
			return ingredient.ingredient;
		}
		if (ingredient.name) {
			return ingredient.name;
		}
		return "Unknown Ingredient";
	};

	// Helper function to get ingredient unit
	const getIngredientUnit = (ingredient: any): string => {
		if (typeof ingredient.unit === "string") {
			return ingredient.unit;
		}
		if (ingredient.unit?.value) {
			return ingredient.unit.value;
		}
		if (ingredient.unit?.label) {
			return ingredient.unit.label;
		}
		return "g"; // Default unit
	};

	// Helper function to get ingredient weight
	const getIngredientWeight = (ingredient: any): number => {
		if (typeof ingredient.weight === "number") {
			return ingredient.weight;
		}
		return parseFloat(ingredient.weight) || 0;
	};

	// Helper function to calculate percentage
	const getIngredientPercentage = (ingredient: any, totalWeight: number): number => {
		if (ingredient.percentage !== undefined && ingredient.percentage !== null) {
			return typeof ingredient.percentage === "number" 
				? ingredient.percentage 
				: parseFloat(ingredient.percentage) || 0;
		}
		if (totalWeight > 0) {
			const weight = getIngredientWeight(ingredient);
			return (weight / totalWeight) * 100;
		}
		return 0;
	};

	// Helper function to get short explanation for ingredient
	const getIngredientExplanation = (ingredient: any): string => {
		// Check if there's a rationale or explanation field
		if (ingredient.rationale) {
			return ingredient.rationale;
		}
		if (ingredient.explanation) {
			return ingredient.explanation;
		}
		if (ingredient.notes) {
			return ingredient.notes;
		}
		// Check dietary information if available
		if (ingredient.ingredient?.dietary && Array.isArray(ingredient.ingredient.dietary) && ingredient.ingredient.dietary.length > 0) {
			return `Dietary: ${ingredient.ingredient.dietary.join(", ")}`;
		}
		// Default explanation based on percentage
		const percentage = getIngredientPercentage(ingredient, totalIngredientWeights);
		if (percentage > 20) {
			return "Primary ingredient";
		} else if (percentage > 5) {
			return "Secondary ingredient";
		} else {
			return "Minor ingredient";
		}
	};

	return (
		<Modal isOpen={isOpen} toggle={handleClose} size="lg" className="generate-product-modal">
			<ModalHeader toggle={handleClose}>Generate New Product</ModalHeader>
			<ModalBody>
				{!showResult ? (
					<div>
						<Form>
							<FormGroup>
								<Label for="productDescription">Product Description</Label>
								<Input
									type="textarea"
									id="productDescription"
									value={productDescription}
									onChange={(e) => setProductDescription(e.target.value)}
									placeholder="Enter a product description (e.g., 'McCormick Bacon Seasoning', 'Organic gluten-free pasta', 'Low-sodium tomato sauce')"
									rows={4}
									disabled={isGenerating}
								/>
								<small className="form-text text-muted">
									Describe the product you want to generate. Be as specific as possible for better
									results.
								</small>
							</FormGroup>
						</Form>

						{error && (
							<Alert color="danger" className="mt-3">
								{error}
							</Alert>
						)}

						{isGenerating && (
							<div className="text-center mt-3">
								<Spinner color="primary" />
								<p className="mt-2">Generating product data with AI...</p>
							</div>
						)}
					</div>
				) : (
					<div>
						<Alert color="success" className="mb-3">
							<strong>Product Generated Successfully!</strong>
							{generatedProduct?.name && (
								<div className="mt-2">
									<strong>Product Name:</strong> {generatedProduct.name}
								</div>
							)}
						</Alert>

						{/* Product Details Section */}
						<Card className="mb-3">
							<CardHeader>
								<h6 className="mb-0">Product Details</h6>
							</CardHeader>
							<CardBody>
								<div className="row">
									{generatedProduct?.flavor && (
										<div className="col-md-6 mb-2">
											<strong>Flavor:</strong> {generatedProduct.flavor}
										</div>
									)}
									{generatedProduct?.product_form && (
										<div className="col-md-6 mb-2">
											<strong>Product Form:</strong> {generatedProduct.product_form}
										</div>
									)}
									{generatedProduct?.product_class && (
										<div className="col-md-6 mb-2">
											<strong>Product Class:</strong> {generatedProduct.product_class}
										</div>
									)}
									{generatedProduct?.product_type && (
										<div className="col-md-6 mb-2">
											<strong>Product Type:</strong> {
												typeof generatedProduct.product_type === 'object' 
													? generatedProduct.product_type.title || generatedProduct.product_type.category
													: generatedProduct.product_type
											}
										</div>
									)}
									{generatedProduct?.serving_size && (
										<div className="col-md-6 mb-2">
											<strong>Serving Size:</strong> {generatedProduct.serving_size} {generatedProduct.unit_serving_size || 'g'}
										</div>
									)}
									{generatedProduct?.cost !== undefined && generatedProduct?.cost !== null && (
										<div className="col-md-6 mb-2">
											<strong>Cost:</strong> {
												generatedProduct.currency?.alpha_3 || generatedProduct.currency?.name || '$'
											}{generatedProduct.cost.toFixed(2)}
										</div>
									)}
									{generatedProduct?.company && (
										<div className="col-md-6 mb-2">
											<strong>Company:</strong> {
												typeof generatedProduct.company === 'object' 
													? generatedProduct.company.name 
													: 'N/A'
											}
										</div>
									)}
									{generatedProduct?.upc_code && (
										<div className="col-md-6 mb-2">
											<strong>Barcode:</strong> 
											<span style={{ fontFamily: 'monospace', fontWeight: 'bold', marginLeft: '8px' }}>
												{generatedProduct.upc_code}
											</span>
										</div>
									)}
									{generatedProduct?.country && (
										<div className="col-md-6 mb-2">
											<strong>Country:</strong> {
												typeof generatedProduct.country === 'object' 
													? generatedProduct.country.name 
													: generatedProduct.country
											}
										</div>
									)}
									{generatedProduct?.notes && (
										<div className="col-12 mb-2">
											<strong>Notes:</strong> 
											<div style={{ 
												whiteSpace: 'pre-wrap', 
												marginTop: '8px',
												padding: '12px',
												backgroundColor: '#f8f9fa',
												borderRadius: '6px',
												border: '1px solid #e9ecef',
												lineHeight: '1.6'
											}}>
												{generatedProduct.notes}
											</div>
										</div>
									)}
									{generatedProduct?.is_ai_generated && (
										<div className="col-md-6 mb-2">
											<strong>Source:</strong> Journey Foods
										</div>
									)}
									{generatedProduct?.is_ai_generated && generatedProduct?.created_date && (
										<div className="col-md-6 mb-2">
											<strong>Date Generated:</strong> {new Date(generatedProduct.created_date).toDateString()}
										</div>
									)}
								</div>
							</CardBody>
						</Card>

						{/* Scores Section */}
						{(generatedProduct?.nutrition_score !== undefined || 
						  generatedProduct?.sustainability_score !== undefined || 
						  generatedProduct?.guava_score !== undefined) && (
							<Card className="mb-3">
								<CardHeader>
									<h6 className="mb-0">Product Scores</h6>
								</CardHeader>
								<CardBody>
									<div className="row">
										{generatedProduct?.nutrition_score !== undefined && generatedProduct?.nutrition_score !== null && (
											<div className="col-md-4 mb-2">
												<div className="d-flex align-items-center">
													<div className="score-badge nutrition mr-2">
														<strong>N</strong>
													</div>
													<div>
														<small className="text-muted d-block">Nutrition Score</small>
														<strong>{generatedProduct.nutrition_score.toFixed(0)}%</strong>
													</div>
												</div>
											</div>
										)}
										{generatedProduct?.sustainability_score !== undefined && generatedProduct?.sustainability_score !== null && (
											<div className="col-md-4 mb-2">
												<div className="d-flex align-items-center">
													<div className="score-badge sustainability mr-2">
														<strong>S</strong>
													</div>
													<div>
														<small className="text-muted d-block">Sustainability Score</small>
														<strong>{generatedProduct.sustainability_score.toFixed(0)}%</strong>
													</div>
												</div>
											</div>
										)}
										{generatedProduct?.guava_score !== undefined && generatedProduct?.guava_score !== null && (
											<div className="col-md-4 mb-2">
												<div className="d-flex align-items-center">
													<div className="score-badge guava mr-2">
														<strong>G</strong>
													</div>
													<div>
														<small className="text-muted d-block">Guava Score</small>
														<strong>{generatedProduct.guava_score.toFixed(0)}</strong>
													</div>
												</div>
											</div>
										)}
									</div>
								</CardBody>
							</Card>
						)}

						{/* Ingredients Details Section */}
						{normalizedIngredients && normalizedIngredients.length > 0 ? (
							<Card className="mb-3">
								<CardHeader>
									<h6 className="mb-0">Ingredient Details</h6>
								</CardHeader>
								<CardBody>
									<div className="table-responsive">
										<Table striped hover size="sm">
											<thead>
												<tr>
													<th>Ingredient</th>
													<th>Quantity</th>
													<th>Percentage</th>
													<th>Explanation</th>
												</tr>
											</thead>
											<tbody>
												{normalizedIngredients.map((ingredient: any, index: number) => {
													const ingredientName = getIngredientName(ingredient);
													const ingredientWeight = getIngredientWeight(ingredient);
													const ingredientUnit = getIngredientUnit(ingredient);
													const ingredientPercentage = getIngredientPercentage(ingredient, totalIngredientWeights);
													const explanation = getIngredientExplanation(ingredient);

													return (
														<tr key={ingredient.ingredient?.id || ingredient.id || index}>
															<td>
																<strong>{ingredientName}</strong>
															</td>
															<td>
																{ingredientWeight > 0 
																	? `${ingredientWeight} ${ingredientUnit}`
																	: "N/A"
																}
															</td>
															<td>
																{ingredientPercentage > 0 
																	? `${ingredientPercentage.toFixed(1)}%`
																	: "N/A"
																}
															</td>
															<td>
																<small className="text-muted">{explanation}</small>
															</td>
														</tr>
													);
												})}
											</tbody>
										</Table>
									</div>
								</CardBody>
							</Card>
						) : (
							<Card className="mb-3">
								<CardHeader>
									<h6 className="mb-0">Ingredient Details</h6>
								</CardHeader>
								<CardBody>
									<p className="text-muted mb-0">
										<small>
											No ingredient details available for this product. Ingredients will be available after the product is saved and processed.
										</small>
									</p>
								</CardBody>
							</Card>
						)}
					</div>
				)}
			</ModalBody>
			<ModalFooter>
				{!showResult ? (
					<>
						<Button color="secondary" onClick={handleClose} disabled={isGenerating}>
							Cancel
						</Button>
						<Button
							color="primary"
							onClick={handleGenerate}
							disabled={isGenerating || !productDescription.trim()}
						>
							{isGenerating ? (
								<>
									<Spinner size="sm" className="mr-2" />
									Generating...
								</>
							) : (
								"Generate Product"
							)}
						</Button>
					</>
				) : (
					<>
						<Button color="secondary" onClick={handleNewGeneration}>
							Generate Another
						</Button>
						<Button color="success" onClick={handleUseProduct}>
							Use This Product
						</Button>
					</>
				)}
			</ModalFooter>
		</Modal>
	);
};

export default GenerateProductModal;
